import React from 'react';
import RemoveFromSectionMenuItem from './RemoveFromSectionMenuItem';
import ReorderTopicsModal from './ReorderTopicsModal';
import { isPremadeSectionId } from './SidebarSectionsProvider';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import { MenuItem, MenuSeparator } from '../components/Menu';
import { TopicPrivacy } from '../graphql/generated/graphql';
import drillDownIcon from '../icons/non-prefilled/16x16/drillDown.svg';
import editIcon from '../icons/non-prefilled/16x16/editPost.svg';
import reorderIcon from '../icons/non-prefilled/16x16/reorder.svg';
import topicIcon from '../icons/non-prefilled/16x16/topic.svg';
import CreatePostMenuItem from '../post/CreatePostMenuItem';
import EditTopicModal from '../topic/modal/EditTopicModal';
import NewTopicModal from '../topic/modal/NewTopicModal';
import topicPolicyCheck, { TopicPolicyAction } from '../topic/topicPolicyCheck';
import type { AllTopicsTreeListTopic } from '../topic/useAllTopics';
import { UIActions } from '../ui/uiSlice';
import topicPath from '../utils/url/topicPath';
import { usePublicContentPermissions } from '../utils/useAllowPublicContentBy';

interface TopicItemMenuItemsProps {
  topic: AllTopicsTreeListTopic;
  sectionId: string;
}

const TopicItemMenuItems = ({ topic, sectionId }: TopicItemMenuItemsProps) => {
  const currentUser = useAppSelector((state) => state.session.user);
  const dispatch = useAppDispatch();

  const { isPermittedToCreatePublicSubtopic } = usePublicContentPermissions();

  const canEditTopic = topicPolicyCheck(
    TopicPolicyAction.EditTopic,
    topic,
    currentUser,
  );

  const handleNewSubtopic =
    topicPolicyCheck(TopicPolicyAction.AddSubtopic, topic, currentUser) &&
    (() =>
      dispatch(UIActions.openModal(NewTopicModal, { parentId: topic.id })));

  const handleEditTopic =
    canEditTopic &&
    (() => dispatch(UIActions.openModal(EditTopicModal, { topic })));

  const handleReorderSubtopics = () => {
    dispatch(
      UIActions.openModal(ReorderTopicsModal, {
        topics: topic.childTopics,
        parentTopic: topic,
      }),
    );
  };

  const canCreatePost = topicPolicyCheck(
    TopicPolicyAction.EditPost,
    topic,
    currentUser,
  );

  const isSubtopicPermitted =
    isPermittedToCreatePublicSubtopic || topic.privacy !== TopicPrivacy.Public;

  return (
    <>
      {canCreatePost && <CreatePostMenuItem topicId={topic.id} />}
      {handleNewSubtopic && (
        <MenuItem
          disabled={!isSubtopicPermitted}
          tooltip={
            isSubtopicPermitted
              ? undefined
              : 'Only admins can\ncreate subtopics\nof public topic'
          }
          icon={topicIcon}
          onClick={handleNewSubtopic}
        >
          Create subtopic
        </MenuItem>
      )}
      {!isPremadeSectionId(sectionId) && (
        <RemoveFromSectionMenuItem sectionId={sectionId} itemId={topic.id} />
      )}
      {handleEditTopic && (
        <MenuItem icon={editIcon} onClick={handleEditTopic}>
          Edit topic
        </MenuItem>
      )}
      {canEditTopic && topic.childTopics.length > 1 && (
        <MenuItem icon={reorderIcon} onClick={handleReorderSubtopics}>
          Reorder subtopics
        </MenuItem>
      )}
      <MenuSeparator />
      <MenuItem icon={drillDownIcon} to={topicPath(topic)}>
        View topic
      </MenuItem>
    </>
  );
};

export default TopicItemMenuItems;
